// CSV Download Utils

import { format } from 'date-fns';

import { Certificate, ComplianceType } from '@/types';

export type CSVRow = {
  name: string;
  email: string;
};

/** Characters that can trigger formula execution in spreadsheet applications */
const csvBacklistChars = ['=', '+', '-', '@', '\t', '\r'];
export const encodeCellValue = (cell: string) => {
  // escape to prevent formula execution in spreadsheet applications to fix CWE-1236:
  if (csvBacklistChars.includes(cell[0])) {
    cell = `'${cell}`;
  }

  // Check if the cell needs to be quoted due to special charaters
  if (/[",]/.test(cell)) {
    // Escape double quotes by doubling them (CSV standard)
    cell = cell.replace(/"/g, '""');
    // Wrap in double quotes
    cell = `"${cell}"`;
  }

  return cell;
};

export const rowsToInlineCsv = (rows: (string | number | boolean)[][]) => {
  let csvContent = '';

  rows.forEach(rowArray => {
    const row = rowArray.join(',');
    csvContent += row + '\r\n';
  });

  const universalBOM = '\uFEFF';
  return `data:text/csv; charset=utf-8,${encodeURIComponent(universalBOM + csvContent)}`;
};

// CSV Upload Utils
export const transformCsvData = (data: unknown[]) => {
  const parsedData: CSVRow[] = [];

  for (const row of data) {
    if (!isValidRowObject(row)) return null;
    const transformedRow = lowerCaseKeys(row);
    if (!hasRequiredFields(transformedRow)) return null;
    parsedData.push({
      name: transformedRow.name.trim(),
      email: transformedRow.email.trim(),
    });
  }

  return parsedData;
};

const isValidRowObject = (row: unknown): row is Record<string, unknown> => {
  return (
    typeof row === 'object' &&
    Object.prototype.toString.call(row) === '[object Object]'
  );
};

const hasRequiredFields = (row: Record<string, unknown>): boolean => {
  return 'name' in row && 'email' in row;
};

const lowerCaseKeys = (obj: Record<string, unknown>) => {
  const result: Record<string, string> = {};
  Object.entries(obj).forEach(([key, value]) => {
    result[key.trim().toLowerCase()] = String(value);
  });
  return result;
};

export const formatTrackerExportFileName = ({
  fromDate,
  toDate,
  extension,
}: {
  fromDate: string;
  toDate: string;
  extension: string;
}) => {
  return `lumiq_tracker_${format(new Date(fromDate), 'yyyyMMdd')}_${format(new Date(toDate), 'yyyyMMdd')}_exported_${format(new Date(), 'yyyy-MM-dd')}.${extension}`;
};

export const transformTrackerDataToCsv = (
  credits: Certificate[],
  compliance: ComplianceType,
) => {
  return [
    [
      'Title',
      'LumiQ Episode ID',
      'Lesson Description',
      compliance === 'CPD' ? 'Total Hours' : 'Credits',
      ...(compliance === 'CPD' ? ['Ethics Hours'] : []),
      ...(compliance === 'CPE' ? ['Field of Study', 'State'] : []),
      'Delivery Method',
      'Provider',
      'Date Completed',
    ],
    ...(credits?.map(c => [
      c.title
        ? encodeCellValue(c.episodeId ? c.title.split('LumiQ - ')[1] : c.title)
        : '',
      c.episodeId ? encodeCellValue(c.episodeId) : '-',
      c.description ? encodeCellValue(c.description) : '-',
      c.credit,
      ...(compliance === 'CPD' ? [c.ethicsCredit] : []),
      ...(compliance === 'CPE'
        ? [c.fieldsOfStudy?.[0].name ?? '', c.location ?? '']
        : []),
      c.type === 'lumiq'
        ? compliance === 'CPE'
          ? 'QAS Self-Study'
          : 'E-Learning'
        : (c.deliveryMethod ?? ''),
      c.type === 'lumiq' ? 'LumiQ Inc.' : (c.provider ?? c.otherProvider ?? ''),
      new Date(c.dateCompleted).toLocaleDateString(),
    ]) ?? []),
  ];
};
